import React, { useState } from "react";
import { Link } from "react-router-dom";
import { timeDifference } from "../../utils/timeUtils";
import { handleImageError, pickClusterImage } from "../../utils/imageUtils";
import { fetchClusterSummary } from "../../services/clusterSummaryService";

/** Rose-tinted AI summary panel. Shows a spinner while generating, an error
 *  message on failure, otherwise the AI summary text. */
function AiSummaryPanel({ text, loading, error, onClose }) {
  return (
    <div className="mt-4 rounded-card bg-ai-bg p-4">
      <div className="mb-2 flex items-center gap-2">
        <span className="grid h-[20px] w-[20px] place-items-center rounded-md bg-ai text-[11px] font-bold text-white">
          ✦
        </span>
        <span className="font-head text-[11px] font-bold uppercase tracking-wider text-ai">
          Përmbledhje nga AI
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto rounded-pill bg-white/60 px-2 py-0.5 text-[10.5px] font-semibold text-ai hover:bg-white"
          title="Mbyll përmbledhjen"
        >
          mbyll ×
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-1 text-[13px] text-sub">
          <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-ai border-t-transparent" />
          Po gjenerohet përmbledhja…
        </div>
      ) : error ? (
        <p className="text-[13px] text-ai">{error}</p>
      ) : (
        <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-ink">
          {text}
        </p>
      )}
    </div>
  );
}

function CountChip({ articleCount, sourceCount }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-pill bg-ai-bg px-3 py-1.5 font-head text-[12px] font-bold text-ai">
      <span aria-hidden>◆</span>
      {articleCount} lajme · {sourceCount} burime
    </span>
  );
}

function SourceChips({ sources, max = 5 }) {
  if (!sources || sources.length === 0) return null;
  const shown = sources.slice(0, max);
  const more = sources.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-head text-[10.5px] font-semibold uppercase tracking-wider text-sub">
        Burimet:
      </span>
      {shown.map((s) => (
        <span
          key={s}
          className="rounded-pill bg-chip px-2.5 py-1 text-[11.5px] text-chip-ink"
        >
          {s}
        </span>
      ))}
      {more > 0 && (
        <span className="rounded-pill bg-chip px-2.5 py-1 text-[11.5px] text-chip-ink">
          +{more}
        </span>
      )}
    </div>
  );
}

function AiPills({ compact = false, onSummarize, onAsk }) {
  const size = compact
    ? "text-[11px] px-3 py-1.5"
    : "text-[12.5px] px-3.5 py-2";
  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        onClick={onSummarize}
        className={`rounded-pill bg-ai font-head font-bold text-white transition hover:bg-brand-2 ${size}`}
      >
        ✦ Përmblidh
      </button>
      {/* TODO: re-enable when "Pyet AI" backend is wired up
      {!compact && (
        <button
          type="button"
          onClick={onAsk}
          className="rounded-pill border-[1.5px] border-ai px-3.5 py-2 font-head text-[12.5px] font-bold text-ai transition hover:bg-ai-bg"
        >
          Pyet AI
        </button>
      )}
      */}
    </div>
  );
}

/**
 * Maps a backend cluster ({ articles: [...] }) into the redesign Cluster shape.
 * Backend data shape is preserved — we only derive presentation fields here.
 */
export function clusterFromBackend(category, clusterId, clusterData) {
  const articles = clusterData?.articles || [];
  const first = articles[0] || {};
  const sources = [...new Set(articles.map((a) => a.portal).filter(Boolean))];
  const imageUrl = pickClusterImage(articles) || "/fallback.jpg";

  return {
    id: clusterId,
    category,
    kicker: category ? category.toUpperCase() : "",
    title: first.title || "",
    deck: first.summary || "",
    // AI-synthesized cluster summary (from backend `cluster_summary` column,
    // exposed as `summary` on the cluster object). Empty string if not yet
    // generated by the clustering pipeline.
    aiSummary: clusterData?.summary || "",
    imageUrl,
    timeAgo: first.fetch_date ? timeDifference(first.fetch_date) : "",
    articleCount: articles.length,
    sourceCount: sources.length,
    sources,
    primaryLink: first.link || "#",
    primaryPortal: first.portal || "",
    relatedArticles: articles.slice(1, 4),
    portalChips: articles.slice(1, 8),
  };
}

export default function StoryCluster({
  cluster,
  variant = "compact",
  detailHref,
  onAsk,
}) {
  const [showSummary, setShowSummary] = useState(false);
  // On-demand summary state — fetched the first time the user clicks Përmblidh,
  // then kept in component state so re-toggling is instant (no second call).
  const [summary, setSummary] = useState(cluster?.aiSummary || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSummarize = async () => {
    // Toggle off if already open.
    if (showSummary) {
      setShowSummary(false);
      return;
    }
    setShowSummary(true);

    // Already have it (pre-generated by pipeline, or fetched earlier).
    if (summary || loading) return;

    setLoading(true);
    setError(null);
    try {
      const data = await fetchClusterSummary(cluster.category, cluster.id);
      if (data && data.summary) {
        setSummary(data.summary);
      } else {
        setError("Përmbledhja nuk u gjenerua dot për momentin.");
      }
    } catch (e) {
      console.warn("cluster-summary failed:", e.message);
      setError("Gabim gjatë gjenerimit. Provoni përsëri.");
    } finally {
      setLoading(false);
    }
  };

  if (!cluster) return null;

  if (variant === "hero") {
    return (
      <article className="overflow-hidden rounded-card border border-line bg-surface">
        <div className="relative h-[300px] bg-gradient-to-br from-[#7fb3c9] via-[#3d6f8a] to-[#22384a]">
          <img
            src={cluster.imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            className="h-full w-full object-cover"
            onError={(e) => handleImageError(e, cluster.imageUrl)}
          />
          {cluster.kicker && (
            <span className="absolute left-4 top-4 rounded-md bg-brand px-2.5 py-1.5 font-head text-[11px] font-bold uppercase tracking-wider text-white">
              {cluster.kicker}
            </span>
          )}
        </div>
        <div className="p-6 lg:p-7">
          <a
            href={cluster.primaryLink}
            target="_blank"
            rel="noopener noreferrer"
            className="block hover:underline"
          >
            <h1 className="font-head text-[26px] font-bold leading-[1.15] tracking-tight text-ink lg:text-[32px]">
              {cluster.title}
            </h1>
          </a>
          {cluster.deck && (
            <p className="mt-3 line-clamp-4 max-w-[62ch] text-[14.5px] leading-relaxed text-sub">
              {cluster.deck}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <CountChip
              articleCount={cluster.articleCount}
              sourceCount={cluster.sourceCount}
            />
            <span className="text-[12.5px] text-sub">{cluster.timeAgo}</span>
            {detailHref && cluster.articleCount > 3 && (
              <Link
                to={detailHref}
                className="ml-auto font-head text-[12.5px] font-semibold text-brand hover:underline"
              >
                Të gjitha lajmet →
              </Link>
            )}
          </div>
          <div className="mt-3.5">
            <SourceChips sources={cluster.sources} />
          </div>
          <div className="mt-4 border-t border-dashed border-line pt-4">
            <AiPills onSummarize={handleSummarize} onAsk={onAsk} />
          </div>
          {showSummary && (
            <AiSummaryPanel
              text={summary}
              loading={loading}
              error={error}
              onClose={() => setShowSummary(false)}
            />
          )}
        </div>
      </article>
    );
  }

  // compact
  return (
    <article className="grid grid-cols-[110px_1fr] gap-4 border-t border-line py-5 first:border-t-0 md:grid-cols-[150px_1fr] md:gap-5">
      <div className="h-[80px] overflow-hidden rounded-card bg-gradient-to-br from-[#7fb3c9] via-[#3d6f8a] to-[#22384a] md:h-[104px]">
        <img
          src={cluster.imageUrl}
          alt=""
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover"
          onError={(e) => handleImageError(e, cluster.imageUrl)}
        />
      </div>
      <div className="min-w-0">
        {cluster.kicker && (
          <span className="font-head text-[10.5px] font-semibold uppercase tracking-wider text-sub">
            {cluster.kicker}
          </span>
        )}
        <a
          href={cluster.primaryLink}
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:underline"
        >
          <h3 className="mt-1 font-head text-[16px] font-bold leading-tight tracking-tight text-ink md:text-[19px]">
            {cluster.title}
          </h3>
        </a>
        {cluster.deck && (
          <p className="mt-2 line-clamp-3 text-[13.5px] leading-relaxed text-sub">
            {cluster.deck}
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2.5">
          <CountChip
            articleCount={cluster.articleCount}
            sourceCount={cluster.sourceCount}
          />
          <span className="text-[12px] text-sub">{cluster.timeAgo}</span>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <SourceChips sources={cluster.sources} max={4} />
          <AiPills compact onSummarize={handleSummarize} onAsk={onAsk} />
          {detailHref && cluster.articleCount > 3 && (
            <Link
              to={detailHref}
              className="font-head text-[12px] font-semibold text-brand hover:underline"
            >
              Të gjitha →
            </Link>
          )}
        </div>
        {showSummary && (
          <div className="col-span-2">
            <AiSummaryPanel
              text={summary}
              loading={loading}
              error={error}
              onClose={() => setShowSummary(false)}
            />
          </div>
        )}
      </div>
    </article>
  );
}
